import type { ReactNode } from "react";
import { useStorefront } from "../app/StorefrontContext.js";
import type { CheckoutLine } from "../lib/publicContract.js";
import { ItemIdentity } from "./ObjectDossier.js";
import { PublicImage } from "./PublicImage.js";
import { Identifier } from "./primitives.js";

/** Descriptive memory only. Core's line IDs and amounts remain authoritative. */
export function ObservedCopy({ itemID, sku }: { itemID: string; sku?: string }): ReactNode {
  const { observedItems } = useStorefront();
  const observed = observedItems[itemID];
  if (observed === undefined || (sku !== undefined && observed.sku !== sku)) {
    return <div className="identity">
      <p className="identity-title">{sku === undefined ? "Saved copy" : `SKU ${sku}`}</p>
      {sku === undefined ? <p className="t-id">{itemID}</p> : null}
      <p className="t-body-s t-secondary">Descriptive details are unavailable in this tab. Keep this reference when asking about the copy.</p>
    </div>;
  }
  return <div className="purchase-copy">
    {observed.public_derivatives[0] === undefined ? null : <PublicImage className="purchase-copy-photo" src={observed.public_derivatives[0]} alt={`Previously viewed photograph of ${observed.title}, SKU ${observed.sku}`} />}
    <div className="stack">
      <ItemIdentity title={observed.title} sku={observed.sku} gradingCompany={observed.grading_company} grade={observed.grade} certNumber={observed.cert_number} />
      {sku === undefined ? null : <p className="t-body-s t-secondary">Previously viewed copy details. The order reference and amounts below come from your checkout.</p>}
    </div>
  </div>;
}

export function CheckoutCopy({ line }: { line: CheckoutLine }): ReactNode {
  const { observedItems } = useStorefront();
  const observed = observedItems[line.physical_item_id];
  const context = observed?.sku === line.sku ? observed : undefined;
  return <div className="stack">
    <div className="identity">
      <p className="identity-title t-body-m">{line.display_name ?? `SKU ${line.sku}`}</p>
      {line.display_name === undefined ? null : <p className="identity-ids"><span className="t-label">SKU</span> <Identifier label="SKU" value={line.sku} /></p>}
    </div>
    {context === undefined ? <p className="t-body-s t-secondary">Previously viewed grade, certification and photographs are unavailable in this tab.</p> : <div className="stack">
      <p className="t-body-s t-secondary">Previously viewed copy details, for context only. Grade, certification and photographs are not saved in this order summary.</p>
      <div className="purchase-copy">
        {context.public_derivatives[0] === undefined ? null : <PublicImage className="purchase-copy-photo" src={context.public_derivatives[0]} alt={`Previously viewed photograph, SKU ${line.sku}`} />}
        <div className="identity">
          {context.grading_company === undefined || context.grade === undefined ? null : <p className="t-id">{context.grading_company} {context.grade}</p>}
          {context.cert_number === undefined ? null : <p>Cert <Identifier label="Previously viewed certification number" value={context.cert_number} /></p>}
        </div>
      </div>
    </div>}
  </div>;
}

export function SelectedCopies(): ReactNode {
  const { cart } = useStorefront();
  return <aside className="summary selected-copies" aria-labelledby="selected-copies-title">
    <h2 className="t-display-xs" id="selected-copies-title">Your exact copies</h2>
    <ul className="summary-lines">{cart.map((id) => <li key={id}><ObservedCopy itemID={id} /></li>)}</ul>
    <p className="t-body-s t-secondary">Prices, shipping and tax are confirmed after you reserve.</p>
  </aside>;
}

export function PurchaseProgress({ current }: { current: "Delivery" | "Review total" | "PayPal" | "Confirmation" | null }): ReactNode {
  return <ol className="purchase-progress" aria-label="Purchase steps">
    {(["Delivery", "Review total", "PayPal", "Confirmation"] as const).map((step, index) => (
      <li key={step} aria-current={current === step ? "step" : undefined}>
        <span aria-hidden="true">{index + 1}</span> {step}
      </li>
    ))}
  </ol>;
}
